import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { Copy, X, ArrowRight, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function daysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }

export function DuplicateMonthDialog({ open, onClose, sourceMonthDate }) {
  const { events, eventsApi } = useApp();
  const [srcYear, setSrcYear] = useState(() => sourceMonthDate.getFullYear());
  const [srcMonth, setSrcMonth] = useState(() => sourceMonthDate.getMonth());
  const [tgtYear, setTgtYear] = useState(() => (sourceMonthDate.getMonth() === 11 ? sourceMonthDate.getFullYear() + 1 : sourceMonthDate.getFullYear()));
  const [tgtMonth, setTgtMonth] = useState(() => (sourceMonthDate.getMonth() + 1) % 12);
  const [busy, setBusy] = useState(false);

  const sourceEvents = useMemo(() => events.filter((ev) => {
    if (ev.recurrence && ev.recurrence !== "none") return false;
    const parts = ev.date.split("-").map(Number);
    return parts[0] === srcYear && (parts[1] - 1) === srcMonth;
  }), [events, srcYear, srcMonth]);

  const navSrc = (dir) => {
    let m = srcMonth + dir; let y = srcYear;
    if (m < 0) { m = 11; y -= 1; } else if (m > 11) { m = 0; y += 1; }
    setSrcMonth(m); setSrcYear(y);
  };
  const navTgt = (dir) => {
    let m = tgtMonth + dir; let y = tgtYear;
    if (m < 0) { m = 11; y -= 1; } else if (m > 11) { m = 0; y += 1; }
    setTgtMonth(m); setTgtYear(y);
  };

  const sameMonth = srcYear === tgtYear && srcMonth === tgtMonth;
  const totalNew = sameMonth ? 0 : sourceEvents.length;

  const confirm = async () => {
    if (totalNew === 0) return;
    setBusy(true);
    const targetDays = daysInMonth(tgtYear, tgtMonth);
    const list = sourceEvents.map((ev) => {
      const day = Math.min(Number(ev.date.split("-")[2]), targetDays);
      const date = `${tgtYear}-${String(tgtMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      return {
        title: ev.title, description: ev.description || "", date,
        start_minutes: ev.start_minutes, end_minutes: ev.end_minutes,
        category_id: ev.category_id, color: ev.color, icon: ev.icon,
        location: ev.location || "", notes: ev.notes || "",
        recurrence: "none", reminder: ev.reminder ?? null, completed: false,
      };
    });
    try {
      await eventsApi.addBulk(list);
      toast.success(`Copied ${list.length} events from ${MONTHS[srcMonth]} to ${MONTHS[tgtMonth]}`);
      onClose();
    } catch (e) {
      toast.error("Could not copy the month");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 border-0 gap-0 overflow-hidden" style={{ background: "transparent", boxShadow: "none" }}>
        <DialogTitle className="sr-only">Copy Month</DialogTitle>
        <DialogDescription className="sr-only">Copy all events from one month onto another month</DialogDescription>
        <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 320, damping: 26 }}
          className="rounded-3xl overflow-hidden p-5 space-y-4" style={{ background: "var(--bg-sidebar)", border: "1px solid var(--border-subtle)" }} data-testid="duplicate-month-dialog">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "color-mix(in srgb, var(--accent-primary) 20%, transparent)" }}><Copy size={17} style={{ color: "var(--accent-primary)" }} /></div>
              <div>
                <h3 className="font-display text-lg font-bold leading-tight" style={{ color: "var(--text-primary)" }}>Copy Month</h3>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>{sourceEvents.length} one-time event{sourceEvents.length === 1 ? "" : "s"} found</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:opacity-70" style={{ color: "var(--text-muted)" }}><X size={18} /></button>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-2xl p-3" style={{ background: "var(--bg-timeline-track)", border: "1px solid var(--border-subtle)" }}>
              <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>From</p>
              <div className="flex items-center justify-between">
                <button onClick={() => navSrc(-1)} className="w-6 h-6 rounded-lg flex items-center justify-center hover:opacity-70" style={{ color: "var(--text-secondary)" }} data-testid="dupm-prev-src"><ChevronLeft size={14} /></button>
                <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{MONTHS[srcMonth]} {srcYear}</span>
                <button onClick={() => navSrc(1)} className="w-6 h-6 rounded-lg flex items-center justify-center hover:opacity-70" style={{ color: "var(--text-secondary)" }} data-testid="dupm-next-src"><ChevronRight size={14} /></button>
              </div>
            </div>
            <ArrowRight size={16} style={{ color: "var(--text-muted)" }} className="flex-none" />
            <div className="flex-1 rounded-2xl p-3" style={{ background: "var(--bg-timeline-track)", border: "1px solid var(--border-subtle)" }}>
              <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>To</p>
              <div className="flex items-center justify-between">
                <button onClick={() => navTgt(-1)} className="w-6 h-6 rounded-lg flex items-center justify-center hover:opacity-70" style={{ color: "var(--text-secondary)" }} data-testid="dupm-prev-tgt"><ChevronLeft size={14} /></button>
                <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{MONTHS[tgtMonth]} {tgtYear}</span>
                <button onClick={() => navTgt(1)} className="w-6 h-6 rounded-lg flex items-center justify-center hover:opacity-70" style={{ color: "var(--text-secondary)" }} data-testid="dupm-next-tgt"><ChevronRight size={14} /></button>
              </div>
            </div>
          </div>

          {sameMonth && <p className="text-xs text-center" style={{ color: "var(--text-muted)" }}>Pick a different target month.</p>}

          <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>Only one-time events are copied. Anything already set to repeat will keep showing up on its own.</p>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onClose} style={{ color: "var(--text-secondary)" }}>Cancel</Button>
            <Button onClick={confirm} disabled={busy || totalNew === 0} className="text-white font-semibold px-6" style={{ background: "var(--accent-primary)" }} data-testid="dupm-confirm">
              <Check size={16} /> {busy ? "Copying..." : `Copy ${totalNew} event${totalNew === 1 ? "" : "s"}`}
            </Button>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
          }
